import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './voucher.css';
import { INSTANCE_WHITE_LOGO_URL as logo } from '../../components/ui/InstanceLogo';
import { MenuQRContext, clearPendingCart, readPendingCart } from '../../context/menu/MenuQRContext';

// Resultado del pago del menú QR: muestra el número de orden.
// - Pago aprobado directo: llega con state { orderNumber, amount, token }.
// - Regreso del 3-D Secure: llega con ?uuid=<clientId> y se consulta el resultado al servidor.

type ResultState = { status: 'loading' | 'approved' | 'failed'; orderNumber?: number; message?: string };

const PaymentResultPage: React.FC = () => {
    const { getPaymentResult } = useContext(MenuQRContext);
    const location = useLocation();
    const navigate = useNavigate();
    const navState = (location.state ?? {}) as { orderNumber?: number; token?: string };

    // Token de la mesa para volver al menú (el pedido pendiente lo guarda antes del 3-D Secure)
    const [menuToken] = useState<string | undefined>(() => navState.token ?? readPendingCart()?.token);
    const [result, setResult] = useState<ResultState>(
        navState.orderNumber ? { status: 'approved', orderNumber: navState.orderNumber } : { status: 'loading' }
    );

    useEffect(() => {
        if (navState.orderNumber) return;

        const uuid = new URLSearchParams(location.search).get('uuid');
        if (!uuid) {
            setResult({ status: 'failed', message: 'No se encontró el pago.' });
            return;
        }

        getPaymentResult(uuid).then(resp => {
            if (resp.status === 'APPROVED') {
                clearPendingCart();
                setResult({ status: 'approved', orderNumber: resp.orderNumber, message: resp.message });
            } else {
                // El pedido se conserva (sessionStorage) para reintentar desde el menú
                setResult({ status: 'failed', message: resp.message });
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const backToMenu = () => navigate(menuToken ? `/menuqr/${menuToken}` : '/', { replace: true });

    return (
        <div className="wrapper1">
            <div className="headerLogin">
                <img src={logo} alt="Logo" className="logo" />
            </div>

            <div className="container mt-5 text-center" style={{ maxWidth: 480, paddingBottom: 100 }}>
                {result.status === 'loading' && (
                    <>
                        <div className="spinner-border text-info mt-4" role="status" />
                        <p className="mt-3 text-muted">Confirmando el pago...</p>
                    </>
                )}

                {result.status === 'approved' && (
                    <div className="card border-0 shadow-sm">
                        <div className="card-body py-5">
                            <i className="bi bi-check-circle-fill text-success" style={{ fontSize: 56 }} />
                            <h3 className="mt-3 mb-1">¡Pago aprobado!</h3>
                            {result.orderNumber ? (
                                <>
                                    <p className="text-muted mb-1">Su número de orden es</p>
                                    <div className="display-4 fw-bold">{result.orderNumber}</div>
                                    <p className="text-muted mt-3 mb-0">Su pedido ya fue enviado al comercio.</p>
                                </>
                            ) : (
                                <div className="alert alert-warning mt-3 mb-0 small">
                                    {result.message || 'El pago fue aprobado. Muestre esta pantalla al personal para confirmar su orden.'}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {result.status === 'failed' && (
                    <div className="card border-0 shadow-sm">
                        <div className="card-body py-5">
                            <i className="bi bi-x-circle-fill text-danger" style={{ fontSize: 56 }} />
                            <h3 className="mt-3 mb-1">El pago no se completó</h3>
                            <p className="text-muted mb-0">{result.message || 'No se realizó ningún cobro.'}</p>
                            <p className="text-muted small mt-2 mb-0">Su pedido se conserva para que pueda intentarlo de nuevo.</p>
                        </div>
                    </div>
                )}
            </div>

            {result.status !== 'loading' && (
                <div style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    backgroundColor: '#fff',
                    padding: '10px',
                    boxShadow: '0 -2px 5px rgba(0,0,0,0.1)',
                    zIndex: 999
                }} className="d-flex justify-content-center">
                    <button type="button" onClick={backToMenu} className="btn btn-primary w-75">
                        {result.status === 'approved' ? 'Volver al menú' : 'Volver al pedido'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default PaymentResultPage;
